import { useUser, useClerk } from "@clerk/clerk-react"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import { useState } from "react"
import { toast } from "sonner"
import { LogOut } from "lucide-react"
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav"

export const AccountPage = () => {
    const { user } = useUser()
    const { signOut } = useClerk()
    const [isLoading, setIsLoading] = useState(false)

    const handleSignOut = async () => {
        setIsLoading(true)
        try {
            await signOut()
            toast.success("Signed out successfully")
        } catch (error) {
            toast.error("Failed to sign out")
            console.error(error)
        } finally {
            setIsLoading(false)
        }
    }

    if (!user) {
        return (
            <main>
                <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
                    <BreadcrumbNav
                        items={[
                            { label: "Home", href: "/" },
                            { label: "Account", href: "/account" },
                        ]}
                        className="mb-6"
                    />
                    <div className="flex items-center justify-center h-64">
                        <div className="flex flex-col items-center gap-4">
                            <Skeleton className="h-8 w-8 rounded-full" />
                            <p className="text-muted-foreground">Loading account data...</p>
                        </div>
                    </div>
                </div>
            </main>
        )
    }

    const initials = user.fullName
        ? user.fullName
              .split(' ')
              .map(name => name[0])
              .join('')
        : user.emailAddresses[0].emailAddress?.charAt(0).toUpperCase()

    return (
        <main className="min-h-screen bg-background">
            <div className="container mx-auto px-6 py-8 max-w-4xl">
                <BreadcrumbNav
                    items={[
                        { label: "Home", href: "/" },
                        { label: "Account", href: "/account" },
                    ]}
                    className="mb-6"
                />
                
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-2xl font-semibold mb-1">Account</h1>
                    <p className="text-muted-foreground">
                        Manage your account settings and profile information
                    </p>
                </div>

                {/* Status Overview */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <div className="text-center">
                        <div className="text-sm text-muted-foreground mb-1">Account Status</div>
                        <div className="font-medium text-green-600">Active</div>
                    </div>
                    <div className="text-center">
                        <div className="text-sm text-muted-foreground mb-1">Email Status</div>
                        <div className={`font-medium ${user.primaryEmailAddress?.verification.status === "verified" ? "text-green-600" : "text-red-600"}`}>
                            {user.primaryEmailAddress?.verification.status === "verified" ? "Verified" : "Unverified"}
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="text-sm text-muted-foreground mb-1">Security</div>
                        <div className="font-medium text-yellow-600">Basic</div>
                    </div>
                    <div className="text-center">
                        <div className="text-sm text-muted-foreground mb-1">Member Since</div>
                        <div className="font-medium">
                            {user.createdAt ? new Date(user.createdAt).getFullYear() : '--'}
                        </div>
                    </div>
                </div>
                {/* Profile Information */}
                <div className="space-y-6">
                    {/* Profile Details */}
                    <div>
                        <h2 className="text-lg font-medium mb-4">Profile Information</h2>
                        <div className="space-y-4">
                            <div className="flex items-center gap-4 py-3">
                                <Avatar className="h-16 w-16">
                                    <AvatarImage src={user.imageUrl} alt={user.fullName || "User"} />
                                    <AvatarFallback className="text-lg">{initials}</AvatarFallback>
                                </Avatar>
                                <div>
                                    <h3 className="font-medium text-lg">{user.fullName || "User"}</h3>
                                    <p className="text-sm text-muted-foreground">{user.primaryEmailAddress?.emailAddress}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Separator />

                    {/* Account Details */}
                    <div>
                        <h2 className="text-lg font-medium mb-4">Account Details</h2>
                        <div className="space-y-3">
                            <div className="flex items-center justify-between py-2">
                                <div>
                                    <div className="font-medium">Email Address</div>
                                    <p className="text-sm text-muted-foreground">{user.primaryEmailAddress?.emailAddress}</p>
                                </div>
                                <Badge 
                                    variant={user.primaryEmailAddress?.verification.status === "verified" ? "outline" : "destructive"} 
                                    className={user.primaryEmailAddress?.verification.status === "verified" ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" : ""}
                                >
                                    {user.primaryEmailAddress?.verification.status === "verified" ? "Verified" : "Unverified"}
                                </Badge>
                            </div>
                            <div className="flex items-center justify-between py-2">
                                <div>
                                    <div className="font-medium">Member Since</div>
                                    <p className="text-sm text-muted-foreground">
                                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center justify-between py-2">
                                <div>
                                    <div className="font-medium">Last Sign In</div>
                                    <p className="text-sm text-muted-foreground">
                                        {user.lastSignInAt ? new Date(user.lastSignInAt).toLocaleDateString() : 'Unknown'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Separator />

                    {/* Security Status */}
                    <div>
                        <h2 className="text-lg font-medium mb-4">Security Status</h2>
                        <div className="space-y-3">
                            <div className="flex items-center justify-between py-2">
                                <div>
                                    <div className="font-medium">Account Status</div>
                                    <p className="text-sm text-muted-foreground">Your account is active and secure</p>
                                </div>
                                <Badge variant="outline" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                    Active
                                </Badge>
                            </div>
                            <div className="flex items-center justify-between py-2">
                                <div>
                                    <div className="font-medium">Two-Factor Authentication</div>
                                    <p className="text-sm text-muted-foreground">Add an extra layer of security</p>
                                </div>
                                <Badge variant="outline" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                                    Not Enabled
                                </Badge>
                            </div>
                            <div className="flex items-center justify-between py-2">
                                <div>
                                    <div className="font-medium">Current Session</div>
                                    <p className="text-sm text-muted-foreground">You are currently signed in</p>
                                </div>
                                <Badge variant="outline" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                    Online
                                </Badge>
                            </div>
                        </div>
                    </div>

                    <Separator />

                    {/* Account Actions */}
                    <div>
                        <h2 className="text-lg font-medium mb-4">Account Actions</h2>
                        <div className="space-y-3">
                            <Button variant="destructive" className="w-full" onClick={handleSignOut} disabled={isLoading}>
                                {isLoading ? (
                                    <>
                                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                        Signing Out...
                                    </>
                                ) : (
                                    <>
                                        <LogOut className="mr-2 h-4 w-4" />
                                        Sign Out
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Information */}
                <div className="mt-8 pt-6 border-t">
                    <p className="text-sm text-muted-foreground">
                        Your account information is managed securely through Clerk authentication. 
                        To update your profile details, email address, or security settings, 
                        please use the account management portal or contact support for assistance.
                    </p>
                </div>
            </div>
        </main>
    );
}